import React from 'react';
import PropTypes from 'prop-types';
import { FaEdit, FaTrash, FaCalculator, FaUndoAlt, FaBoxOpen } from 'react-icons/fa';

const ActionCell = ({ data, onEdit, onDelete, onRecalculate, onBacklog }) => {
  const { allow_edit, allow_delete, allow_recalculate } = data?.permissions || {};
  const isReleased = !!data?.released_to_planning_at;
  const isBacklog  = data?.status === 'backlog';

  // Backlog rows are locked — no actions available
  if (isBacklog) return <div />;

  return (
    <div className="tw-flex tw-gap-1 tw-items-center tw-h-full">
      {allow_edit && (
        <button
          type="button"
          className="tw-px-2 tw-py-1 tw-border tw-border-blue-300 tw-rounded-md tw-bg-white tw-text-blue-600 hover:tw-bg-blue-50 tw-inline-flex tw-items-center tw-cursor-pointer"
          title="Edit / View"
          onClick={() => onEdit(data)}
        >
          <FaEdit size={12} />
        </button>
      )}

      {allow_delete && !isReleased && (
        <button
          type="button"
          className="tw-px-2 tw-py-1 tw-border tw-border-red-300 tw-rounded-md tw-bg-white tw-text-red-600 hover:tw-bg-red-50 tw-inline-flex tw-items-center tw-cursor-pointer"
          title="Delete"
          onClick={() => onDelete(data)}
        >
          <FaTrash size={12} />
        </button>
      )}

      {/* Backlog at once — the page offers Undo; it applies to the whole project. */}
      {allow_edit && onBacklog && !isReleased && (
        <button
          type="button"
          className="tw-px-2 tw-py-1 tw-border tw-border-amber-300 tw-rounded-md tw-bg-white tw-text-amber-700 hover:tw-bg-amber-50 tw-inline-flex tw-items-center tw-cursor-pointer"
          title="Move the project to Backlog (Undo available)"
          aria-label="Move to Backlog"
          onClick={() => onBacklog(data)}
        >
          <FaBoxOpen size={12} />
        </button>
      )}

      {allow_recalculate && onRecalculate && !isReleased && (
        <button
          type="button"
          className="tw-px-2 tw-py-1 tw-border tw-border-yellow-300 tw-rounded-md tw-bg-white tw-text-yellow-600 hover:tw-bg-yellow-50 tw-inline-flex tw-items-center tw-cursor-pointer"
          title="Recalculate"
          onClick={() => onRecalculate(data)}
        >
          <FaCalculator size={12} />
        </button>
      )}
    </div>
  );
};

ActionCell.propTypes = {
  data:          PropTypes.object.isRequired,
  onEdit:        PropTypes.func.isRequired,
  onDelete:      PropTypes.func.isRequired,
  onRecalculate: PropTypes.func,
  onBacklog:     PropTypes.func,
};

// Deleted tab: bring the rows back, or remove them for good. Restore is an undo
// arrow with its name — a second trash-like icon next to the red trash read as
// two ways of deleting.
export const DeletedActionCell = ({ data, onRestore, onPurge }) => {
  const { allow_restore, allow_purge } = data?.permissions || {};
  return (
    <div className="tw-flex tw-gap-1 tw-items-center tw-h-full">
      {allow_restore && (
        <button
          type="button"
          className="tw-px-2 tw-py-1 tw-border tw-border-emerald-300 tw-rounded-md tw-bg-white tw-text-emerald-700 hover:tw-bg-emerald-50 tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-cursor-pointer"
          title="Restore — back to the list"
          onClick={() => onRestore(data)}
        >
          <FaUndoAlt size={11} aria-hidden="true" /> Restore
        </button>
      )}
      {allow_purge && (
        <button
          type="button"
          className="tw-px-2 tw-py-1 tw-border tw-border-red-300 tw-rounded-md tw-bg-white tw-text-red-600 hover:tw-bg-red-50 tw-inline-flex tw-items-center tw-cursor-pointer"
          title="Delete permanently — cannot be undone"
          aria-label="Delete permanently"
          onClick={() => onPurge(data)}
        >
          <FaTrash size={12} />
        </button>
      )}
    </div>
  );
};

DeletedActionCell.propTypes = {
  data:      PropTypes.object.isRequired,
  onRestore: PropTypes.func.isRequired,
  onPurge:   PropTypes.func.isRequired,
};

export default ActionCell;